import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, apiClient } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import DashboardLayout from "@/components/layout/DashboardLayout";
import ProgressMessages from "@/components/ProgressMessages";
import VoiceInput from "@/components/VoiceInput";
import { 
  Quote, 
  ArrowRight, 
  ArrowLeft,
  Loader2,
  Sparkles,
  Check,
  Calendar,
  Globe,
  Layers,
  FileText,
  Link2,
  Video,
  Image,
  Eye
} from "lucide-react";

const platforms = [
  { id: "facebook", name: "Facebook", color: "#1877F2" },
  { id: "instagram", name: "Instagram", color: "#E4405F" },
  { id: "tiktok", name: "TikTok", color: "#000000" },
  { id: "pinterest", name: "Pinterest", color: "#BD081C" }
];

const tones = [
  { id: "professional", label: "Professional", icon: "💼" },
  { id: "casual", label: "Casual", icon: "😊" },
  { id: "humorous", label: "Humorous", icon: "😄" },
  { id: "inspirational", label: "Inspirational", icon: "✨" },
];

// Source types with support for social media posts
const sourceTypes = [
  { id: "website", label: "Website / Blog", icon: Link2, desc: "Any website URL" },
  { id: "facebook", label: "Facebook Post", icon: Globe, desc: "FB posts or reels" },
  { id: "instagram", label: "Instagram Post", icon: Globe, desc: "IG posts or reels" },
  { id: "tiktok", label: "TikTok Video", icon: Video, desc: "TikTok videos" },
  { id: "text", label: "Paste Text", icon: FileText, desc: "Copy/paste content" },
];

export default function QuoteAcrossPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, refreshUser } = useAuth();
  
  // Check if coming from Content Radar
  const fromRadar = searchParams.get("source") === "radar";
  const radarContent = searchParams.get("content") || "";
  const radarUrl = searchParams.get("url") || "";
  const competitorName = searchParams.get("competitor") || "";
  
  // Step state: 1=Single/Bulk, 2=Content Type, 3=Source, 4=Platforms, 5=Tone, 6=Generating, 7=Done
  const [step, setStep] = useState(fromRadar ? 4 : 1);
  
  // User choices
  const [scheduleType, setScheduleType] = useState(fromRadar ? "single" : null);
  const [contentType, setContentType] = useState("post"); // "post" or "video"
  const [sourceType, setSourceType] = useState(fromRadar ? "text" : null);
  const [selectedPlatforms, setSelectedPlatforms] = useState(["facebook", "instagram", "tiktok", "pinterest", "linkedin", "youtube", "twitter"]);
  const [selectedTone, setSelectedTone] = useState("professional");
  
  // Bulk options
  const [days, setDays] = useState(7);
  const [postsPerDay, setPostsPerDay] = useState(1);
  
  // Input data
  const [inputUrl, setInputUrl] = useState(radarUrl);
  const [inputText, setInputText] = useState(radarContent);
  const [fetchedContent, setFetchedContent] = useState(fromRadar ? radarContent : "");
  
  // Generation state
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [progress, setProgress] = useState(0);
  const [generatedPosts, setGeneratedPosts] = useState([]);
  
  // Credits - differentiate between post and video
  const postsRemaining = (user?.posts_remaining || 0) + (user?.payg_posts_remaining || 0);
  const videosRemaining = user?.video_credits_remaining || 0;
  const planValidityDays = user?.plan_validity_days || 90;
  const maxDays = Math.min(planValidityDays, 90);
  
  const isVideoContent = contentType === "video";
  const creditsNeeded = scheduleType === "bulk" 
    ? (isVideoContent ? days : days * postsPerDay * selectedPlatforms.length)
    : selectedPlatforms.length;
  const creditsAvailable = isVideoContent ? videosRemaining : postsRemaining;
  const hasEnoughCredits = creditsNeeded <= creditsAvailable;
  const creditTypeName = isVideoContent ? "video" : "post";

  const togglePlatform = (platformId) => {
    setSelectedPlatforms(prev => {
      if (prev.includes(platformId)) {
        if (prev.length === 1) return prev;
        return prev.filter(p => p !== platformId);
      }
      return [...prev, platformId];
    });
  };

  const fetchContentFromUrl = async () => {
    if (!inputUrl) {
      toast.error("Please enter a URL");
      return;
    }
    
    setFetching(true);
    
    try {
      // Use the quote-across/fetch endpoint for web content extraction
      const response = await apiClient.post("/content/quote-across/fetch", { 
        url: inputUrl 
      });
      
      const content = response.data.content || "";
      
      if (content && content.length > 50) {
        setFetchedContent(content);
        toast.success("Content extracted! Review and continue.");
      } else {
        // Fallback for social media URLs or empty content
        setFetchedContent(`Creating content inspired by: ${inputUrl}`);
        toast.info("Content will be generated based on this source");
      }
    } catch (error) {
      console.error("Fetch error:", error);
      // For non-website sources, still allow continuation with URL reference
      if (sourceType !== "website") {
        setFetchedContent(`Creating original content inspired by this ${sourceType} post: ${inputUrl}`);
        toast.info("Will create original content inspired by this source");
      } else {
        toast.error("Could not fetch content from this URL. Please paste the content manually.");
        setSourceType("text");
      }
    } finally {
      setFetching(false);
    }
  };

  const handleGenerate = async () => {
    const contentToUse = fetchedContent || inputText;
    if (!contentToUse) {
      toast.error("Please provide content first");
      return;
    }

    if (!hasEnoughCredits) {
      toast.error("Not enough credits");
      navigate("/pricing");
      return;
    }

    setLoading(true);
    setStep(6);
    setProgress(0);

    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 90) return 90;
        return Math.min(90, prev + Math.floor(Math.random() * 8) + 3);
      });
    }, 500);

    try {
      const allPosts = [];
      const daysToGenerate = scheduleType === "bulk" ? days : 1;
      const postsPerDayCount = scheduleType === "bulk" ? postsPerDay : 1;

      for (let day = 0; day < daysToGenerate; day++) {
        for (let postNum = 0; postNum < postsPerDayCount; postNum++) {
          const scheduleDate = new Date();
          scheduleDate.setDate(scheduleDate.getDate() + day + 1);
          scheduleDate.setHours(10 + (postNum * 4), 0, 0, 0);

          const formPayload = new FormData();
          formPayload.append("content_type", "write");
          formPayload.append("platforms", selectedPlatforms.join(","));
          
          // Improved prompt that handles both extracted content and URL-only cases
          let prompt;
          if (contentToUse.startsWith("Creating content inspired by:") || contentToUse.startsWith("Creating original content inspired by")) {
            // URL couldn't be scraped - use the URL as inspiration reference
            prompt = `Create engaging social media content with a ${selectedTone} tone. Reference source: ${inputUrl}. The content should be original and ${contentType === "video" ? "suitable for a short video script" : "shareable and engaging"}. Create content that would appeal to someone interested in this type of source. Day ${day + 1}, variation ${postNum + 1}.`;
          } else {
            // We have actual content to adapt
            prompt = `Adapt this content with a ${selectedTone} tone for social media. Original: "${contentToUse}". Make it ${contentType === "video" ? "a video script idea" : "engaging and shareable"}. Day ${day + 1}, variation ${postNum + 1}.`;
          }
          formPayload.append("text", prompt);

          const res = await apiClient.post("/content/generate-ai", formPayload, {
            headers: { "Content-Type": "multipart/form-data" }
          });

          for (const post of res.data.posts) {
            await apiClient.put(`/posts/${post.id}`, {
              scheduled_at: scheduleDate.toISOString(),
              status: "scheduled",
              type: contentType
            });
            allPosts.push(post);
          }
        }
      }

      clearInterval(progressInterval);
      setProgress(100);
      setGeneratedPosts(allPosts);
      await refreshUser();
      setStep(7);
      toast.success(`Created ${allPosts.length} posts!`);

    } catch (error) {
      clearInterval(progressInterval);
      console.error("Generation error:", error);
      toast.error("Failed to generate content");
      setStep(5);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-3xl mx-auto">
        {/* Credits Banner */}
        <div className="mb-6 p-4 rounded-xl bg-white/5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-viraly-orange" />
              <span className="text-sm">
                <span className="text-viraly-orange font-bold">{postsRemaining}</span> post credits
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Video className="w-5 h-5 text-blue-400" />
              <span className="text-sm">
                <span className="text-blue-400 font-bold">{videosRemaining}</span> video credits
              </span>
            </div>
          </div>
        </div>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-2xl bg-viraly-gradient flex items-center justify-center">
              <Quote className="w-6 h-6 text-white" />
            </div>
            <h1 className="font-heading text-3xl font-bold">Quote Across</h1>
          </div>
          <p className="text-viraly-muted">Transform any content into engaging posts</p>
          {fromRadar && competitorName && (
            <p className="text-sm text-viraly-orange mt-2">
              Creating from {competitorName}'s content
            </p>
          )}
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3, 4, 5].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                step >= s ? "bg-viraly-gradient text-white" : "bg-white/10 text-viraly-muted"
              }`}>
                {step > s ? <Check className="w-4 h-4" /> : s}
              </div>
              {s < 5 && <div className={`w-8 h-0.5 mx-1 ${step > s ? "bg-viraly-orange" : "bg-white/10"}`} />}
            </div>
          ))}
        </div>

        {/* STEP 1: Content Type (FIRST) */}
        {step === 1 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="text-center mb-8">
              <p className="text-viraly-muted">What type of content do you want to create?</p>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-8">
              <button
                onClick={() => { setContentType("post"); setStep(2); }}
                className="p-6 rounded-xl border-2 border-white/10 hover:border-viraly-orange/50 bg-white/5 transition-all text-center group"
                data-testid="content-type-post"
              >
                <div className="w-16 h-16 rounded-xl bg-viraly-orange/20 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <Image className="w-8 h-8 text-viraly-orange" />
                </div>
                <h3 className="font-bold text-lg mb-1">Image / Text Post</h3>
                <p className="text-sm text-viraly-muted">Social media posts</p>
                <div className="mt-3 flex items-center justify-center gap-2">
                  <span className="text-viraly-orange font-bold">{postsRemaining}</span>
                  <span className="text-xs text-viraly-muted">post credits</span>
                </div>
              </button>

              <button
                onClick={() => { setContentType("video"); setStep(2); }}
                className="p-6 rounded-xl border-2 border-white/10 hover:border-blue-500/50 bg-white/5 transition-all text-center group"
                data-testid="content-type-video"
              >
                <div className="w-16 h-16 rounded-xl bg-blue-500/20 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <Video className="w-8 h-8 text-blue-400" />
                </div>
                <h3 className="font-bold text-lg mb-1">Video Script</h3>
                <p className="text-sm text-viraly-muted">Video content ideas</p>
                <div className="mt-3 flex items-center justify-center gap-2">
                  <span className="text-blue-400 font-bold">{videosRemaining}</span>
                  <span className="text-xs text-viraly-muted">video credits</span>
                </div>
              </button>
            </div>
          </motion.div>
        )}

        {/* STEP 2: Single or Bulk (SECOND) */}
        {step === 2 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <button onClick={() => setStep(1)} className="flex items-center gap-2 text-viraly-muted hover:text-white mb-6">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>

            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl font-bold mb-2">How Many?</h2>
              <p className="text-viraly-muted">Create one {contentType === "video" ? "video" : "post"} or schedule many?</p>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-8">
              <button
                onClick={() => { setScheduleType("single"); setStep(3); }}
                className="card-base p-6 hover:border-viraly-orange/50 transition-all text-left group"
                data-testid="single-post-btn"
              >
                <div className="w-12 h-12 rounded-xl bg-viraly-orange/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <FileText className="w-6 h-6 text-viraly-orange" />
                </div>
                <h3 className="font-bold mb-1">Single {contentType === "video" ? "Video" : "Post"}</h3>
                <p className="text-sm text-viraly-muted">Transform once, post everywhere</p>
              </button>

              <button
                onClick={() => { setScheduleType("bulk"); setStep(3); }}
                className="card-base p-6 hover:border-viraly-orange/50 transition-all text-left group"
                data-testid="bulk-schedule-btn"
              >
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Layers className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="font-bold mb-1">Bulk Schedule</h3>
                <p className="text-sm text-viraly-muted">Multiple variations over days</p>
              </button>
            </div>
          </motion.div>
        )}

        {/* STEP 3: Source Selection */}
        {step === 3 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <button onClick={() => setStep(2)} className="flex items-center gap-2 text-viraly-muted hover:text-white mb-6">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>

            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl font-bold mb-2">Content Source</h2>
              <p className="text-viraly-muted">Where is the content you want to adapt?</p>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-6">
              {sourceTypes.map((src) => {
                const Icon = src.icon;
                return (
                  <button
                    key={src.id}
                    onClick={() => setSourceType(src.id)}
                    className={`p-4 rounded-xl flex flex-col items-center gap-2 transition-all border-2 ${
                      sourceType === src.id
                        ? "border-viraly-orange bg-viraly-orange/10"
                        : "border-white/10 bg-white/5 hover:border-white/20"
                    }`}
                  >
                    <Icon className={`w-6 h-6 ${sourceType === src.id ? "text-viraly-orange" : "text-viraly-muted"}`} />
                    <span className={`text-sm font-medium ${sourceType === src.id ? "text-white" : ""}`}>{src.label}</span>
                    <span className="text-xs text-viraly-muted">{src.desc}</span>
                  </button>
                );
              })}
            </div>

            {/* URL or Text Input based on source type */}
            {sourceType && sourceType !== "text" && (
              <div className="card-base p-4 mb-6">
                <label className="block text-sm font-medium mb-2">
                  {sourceType === "website" ? "Website URL" : `${sourceType.charAt(0).toUpperCase() + sourceType.slice(1)} Post URL`}
                </label>
                <div className="flex gap-2">
                  <Input
                    type="url"
                    placeholder={
                      sourceType === "website" ? "https://example.com/article" :
                      sourceType === "facebook" ? "https://facebook.com/user/posts/..." :
                      sourceType === "instagram" ? "https://instagram.com/p/..." :
                      "https://tiktok.com/@user/video/..."
                    }
                    value={inputUrl}
                    onChange={(e) => setInputUrl(e.target.value)}
                    className="input-base flex-1"
                  />
                  <Button onClick={fetchContentFromUrl} disabled={fetching || !inputUrl} className="btn-secondary">
                    {fetching ? <Loader2 className="w-4 h-4 animate-spin" /> : "Fetch"}
                  </Button>
                </div>
                {fetchedContent && (
                  <div className="mt-4 p-3 rounded-lg bg-white/5 border border-white/10">
                    <p className="text-xs text-viraly-muted mb-1">Content Preview:</p>
                    <p className="text-sm text-viraly-light line-clamp-3">{fetchedContent}</p>
                  </div>
                )}
              </div>
            )}

            {sourceType === "text" && (
              <div className="card-base p-4 mb-6">
                <label className="block text-sm font-medium mb-2">Paste Content</label>
                <div className="relative">
                  <Textarea
                    placeholder="Paste the content you want to adapt..."
                    value={inputText}
                    onChange={(e) => { setInputText(e.target.value); setFetchedContent(e.target.value); }}
                    className="input-base min-h-[120px] pr-12"
                  />
                  <div className="absolute right-2 top-2">
                    <VoiceInput onTranscription={(text) => { 
                      const newText = inputText ? inputText + " " + text : text;
                      setInputText(newText);
                      setFetchedContent(newText);
                    }} />
                  </div>
                </div>
              </div>
            )}

            <Button
              onClick={() => setStep(4)}
              disabled={!sourceType || (!fetchedContent && !inputText)}
              className="btn-primary w-full py-4"
            >
              Continue <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </motion.div>
        )}

        {/* STEP 4: Platforms */}
        {step === 4 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <button onClick={() => fromRadar ? navigate(-1) : setStep(3)} className="flex items-center gap-2 text-viraly-muted hover:text-white mb-6">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>

            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl font-bold mb-2">Select Platforms</h2>
              <p className="text-viraly-muted">Where do you want to post?</p>
            </div>

            <div className="flex gap-3 mb-6">
              {platforms.map((p) => (
                <button
                  key={p.id}
                  onClick={() => togglePlatform(p.id)}
                  className={`flex-1 p-4 rounded-xl flex flex-col items-center gap-2 transition-all border-2 ${
                    selectedPlatforms.includes(p.id)
                      ? "border-viraly-orange bg-viraly-orange/10"
                      : "border-white/10 bg-white/5 hover:border-white/20"
                  }`}
                >
                  <Globe className="w-6 h-6" style={{ color: selectedPlatforms.includes(p.id) ? p.color : "#888" }} />
                  <span className="font-medium">{p.name}</span>
                  {selectedPlatforms.includes(p.id) && <Check className="w-4 h-4 text-viraly-orange" />}
                </button>
              ))}
            </div>

            {/* Bulk options */}
            {scheduleType === "bulk" && (
              <div className="mb-6 p-4 rounded-xl bg-white/5 space-y-4">
                <div>
                  <div className="flex justify-between mb-2">
                    <label className="text-sm font-medium">Duration</label>
                    <span className="text-viraly-orange font-bold">{days} days</span>
                  </div>
                  <Slider value={[days]} onValueChange={(val) => setDays(val[0])} min={1} max={maxDays} step={1} />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Posts per day per platform</label>
                  <div className="flex gap-2">
                    {[1, 2, 3].map((n) => (
                      <button
                        key={n}
                        onClick={() => setPostsPerDay(n)}
                        className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                          postsPerDay === n ? "bg-viraly-orange text-white" : "bg-white/10 hover:bg-white/20"
                        }`}
                      >
                        {n}x
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Credit Warning */}
            <div className={`mb-6 p-4 rounded-xl border ${
              isVideoContent ? "bg-blue-500/10 border-blue-500/20" : "bg-viraly-orange/10 border-viraly-orange/20"
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {isVideoContent ? (
                    <Video className="w-5 h-5 text-blue-400" />
                  ) : (
                    <FileText className="w-5 h-5 text-viraly-orange" />
                  )}
                  <div>
                    <p className="text-sm font-medium">This will use {creditsNeeded} {creditTypeName} credit{creditsNeeded !== 1 ? 's' : ''}</p>
                    <p className="text-xs text-viraly-muted">
                      {scheduleType === "bulk" 
                        ? `${days} days × ${postsPerDay}/day × ${selectedPlatforms.length} platforms`
                        : `${selectedPlatforms.length} platform(s) selected`}
                    </p>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                  hasEnoughCredits ? 'bg-viraly-success/20 text-viraly-success' : 'bg-red-500/20 text-red-400'
                }`}>
                  {creditsAvailable} {creditTypeName} credits
                </span>
              </div>
            </div>

            <Button onClick={() => setStep(5)} disabled={selectedPlatforms.length === 0} className="btn-primary w-full py-4">
              Continue <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </motion.div>
        )}

        {/* STEP 5: Tone Selection */}
        {step === 5 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <button onClick={() => setStep(4)} className="flex items-center gap-2 text-viraly-muted hover:text-white mb-6">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>

            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl font-bold mb-2">Select Tone</h2>
              <p className="text-viraly-muted">How should your content sound?</p>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-8">
              {tones.map((tone) => (
                <button
                  key={tone.id}
                  onClick={() => setSelectedTone(tone.id)}
                  className={`p-4 rounded-xl flex items-center gap-3 transition-all border-2 ${
                    selectedTone === tone.id
                      ? "border-viraly-orange bg-viraly-orange/10"
                      : "border-white/10 bg-white/5 hover:border-white/20"
                  }`}
                >
                  <span className="text-2xl">{tone.icon}</span>
                  <span className="font-medium">{tone.label}</span>
                  {selectedTone === tone.id && <Check className="w-4 h-4 ml-auto text-viraly-orange" />}
                </button>
              ))}
            </div>

            <Button
              onClick={handleGenerate}
              disabled={!hasEnoughCredits || loading}
              className="btn-primary w-full py-4"
            >
              {loading ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Generating...</>
              ) : (
                <><Sparkles className="w-5 h-5 mr-2" /> Generate ({creditsNeeded} {creditTypeName} credit{creditsNeeded !== 1 ? 's' : ''})</>
              )}
            </Button>
          </motion.div>
        )}

        {/* STEP 6: Generating */}
        {step === 6 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-12">
            <div className="w-20 h-20 rounded-full bg-viraly-gradient flex items-center justify-center mx-auto mb-6 animate-pulse">
              <Sparkles className="w-10 h-10 text-white" />
            </div>
            <h2 className="font-heading text-2xl font-bold mb-2">Transforming Content</h2>
            <p className="text-viraly-muted mb-6">
              Creating {selectedTone} versions for {selectedPlatforms.join(", ")}...
            </p>
            <div className="w-full max-w-xs mx-auto bg-white/10 rounded-full h-3 mb-2">
              <div className="bg-viraly-gradient h-3 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
            </div>
            <p className="text-sm text-viraly-orange">{Math.round(progress)}%</p>
            <ProgressMessages progress={progress} />
          </motion.div>
        )}

        {/* STEP 7: Done */}
        {step === 7 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-viraly-success/20 flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-viraly-success" />
              </div>
              <h2 className="font-heading text-2xl font-bold mb-2">Content Created!</h2>
              <p className="text-viraly-muted">{generatedPosts.length} posts scheduled successfully</p>
            </div>

            {/* Preview */}
            <div className="space-y-3 mb-6 max-h-60 overflow-y-auto">
              {generatedPosts.slice(0, 3).map((post, i) => (
                <div key={i} className="card-base p-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      post.platform === "facebook" ? "bg-blue-500/20" :
                      post.platform === "instagram" ? "bg-pink-500/20" : "bg-black/40"
                    }`}>
                      <Globe className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium capitalize">{post.platform}</p>
                      <p className="text-xs text-viraly-muted line-clamp-1">{post.caption?.substring(0, 60)}...</p>
                    </div>
                  </div>
                </div>
              ))}
              {generatedPosts.length > 3 && (
                <p className="text-center text-sm text-viraly-muted">+{generatedPosts.length - 3} more</p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <Button onClick={() => navigate("/posts?tab=scheduled")} className="btn-primary flex items-center justify-center gap-2">
                <Eye className="w-4 h-4" /> View in Posts
              </Button>
              <Button onClick={() => navigate("/calendar")} className="btn-secondary flex items-center justify-center gap-2">
                <Calendar className="w-4 h-4" /> View Calendar
              </Button>
            </div>

            <Button
              onClick={() => { setStep(1); setGeneratedPosts([]); setFetchedContent(""); setSourceType(null); }}
              className="btn-secondary w-full"
            >
              <Sparkles className="w-4 h-4 mr-2" /> Create More
            </Button>
          </motion.div>
        )}
      </div>
    </DashboardLayout>
  );
}
